import DOMPurify from "dompurify";
import { firstName } from "../../collections/collectorListsApi";
import { QUOTATION_VAT_PERCENT } from "./quotationVat";

// Fixed, one-time-authored opening line - per direct instruction, this is
// NOT a per-quotation editable field (no DB column, no rich-text editor);
// if the wording ever needs to change, it's a code change here, not a
// data-entry field. The greeting itself ("Dear {customer},") is rendered
// separately by QuotationPrintPreview.jsx using the real customer name -
// this string is only the body that follows it.
const COVER_LETTER_TEXT =
  "Thank you for reaching out to us. Following the security assessment of your premises, please find the cost estimate for the requested security personnel / equipment(s) below.";

// Props for components/QuotationPrintPreview.jsx (2026-10-28 - the quotation's
// own design from the customer's sample; until then this fed the shared
// StockDocumentPreview.jsx, whose prop names are kept). signatureImageUrl
// is a data: URI (see blobToDataUrl in buildLpoPrintProps.js, reused as-is for Quotation too). serviceTypes is
// the quotation's own branch's Service Type master list (caller fetches
// via useGetServiceTypesQuery({branchId, isActive:true}) - this function
// stays a plain synchronous mapper, no data fetching of its own).
// enquiryServiceTypes is the linked Sales Enquiry's own selected service
// types (caller fetches via useGetEnquiryQuery(salesEnquiryRecordGuid) when
// the quotation has one) - only used for the single heading line below.
// "23 Oct 2026", as on the customer's sample.
const printDate = (value) => new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export const buildQuotationPrintProps = (quotation, companyInfo, signatureImageUrl, serviceTypes = [], enquiryServiceTypes = []) => {
  const companyName = companyInfo?.["Company.Name"] || "Texas Alarms";
  // Title bar heading. When the
  // quotation is linked to a Sales Enquiry, name the services that enquiry
  // was actually raised for ("QUOTATION FOR FIRE ALARM, CCTV"). Otherwise
  // (a from-scratch quotation, no enquiry link) fall back to a plain
  // "Quotation from {company}" line.
  //
  // 2026-09-19 (reverses the earlier "don't use the prototype name"): a
  // quotation built from a prototype is headed "QUOTATION FOR {prototype}",
  // and that wins over the enquiry's service types - the prototype names the
  // exact system being quoted ("Electric Fence"), the enquiry only the areas
  // of interest.
  const prototypeName = (quotation.prototypeName || "").trim();
  const quotationSubject = prototypeName
    ? `QUOTATION FOR ${prototypeName.toUpperCase()}`
    : enquiryServiceTypes.length > 0
    ? `QUOTATION FOR ${enquiryServiceTypes.map((s) => s.serviceTypeName).join(", ").toUpperCase()}`
    : `Quotation from ${companyName}`;

  const printLines = quotation.lines.map((l) => ({
    qty: l.qty,
    productName: l.description,
    price: l.rate,
    vatAmount: (Number(l.qty) || 0) * (Number(l.rate) || 0) * ((Number(l.vatPercent) || 0) / 100),
    amount: (Number(l.qty) || 0) * (Number(l.rate) || 0),
  }));

  return {
    title: "QUOTATION",
    docNo: quotation.quotationNumber,
    docDate: printDate(quotation.quotationDate),
    // The linked Sales Enquiry/Order number is internal provenance (shown
    // elsewhere in the app's own Quotation Detail page) - not meant for a
    // customer-facing "Reference No" line, per direct instruction.
    refNo: "",
    branchLabel: quotation.branchCode,
    // 4-5 of the branch's own configured Service Type names, joined onto
    // one line for the yellow letterhead banner - real master data instead
    // of a hardcoded tagline, per direct instruction ("we use the service
    // type master items as text").
    // " | "-separated under the banner (2026-10-28 design).
    letterheadBannerText: serviceTypes.slice(0, 5).map((s) => s.serviceTypeName).join(" | "),
    // The maroon title bar (see quotationSubject computation above).
    quotationSubject,
    partyLabel: "Customer",
    partyName: quotation.customerName,
    partyAddress: quotation.customerAddress || "",
    partyMobile: quotation.customerPhone || "",
    // Batch221 - "For further info kindly contact" (employee first name + mobile).
    salespersonName: firstName(quotation.salespersonName),
    salespersonPhone: quotation.salespersonPhone || "",
    // The header block's own small Remarks slot is left blank for
    // Quotation - the rich "Footer Notes" content goes in the dedicated
    // footerNotesHtml block after the line items instead (see below),
    // not squeezed into this narrow party-info cell.
    remarks: "",
    showVat: true,
    // The fixed quotation rate, never one derived from the amounts (2026-10-28):
    // vatAmount / net printed "VAT 11%" whenever some lines carried a product's
    // own 0% VAT. Every line is now charged this rate (LineItemsEditor
    // fixedVatPercent + the API), so the label and the amounts agree for any
    // quotation saved since; an older one shows its stored VAT until re-saved.
    vatPercent: QUOTATION_VAT_PERCENT,
    lines: printLines,
    totals: {
      subTotal: quotation.subTotal,
      vatAmount: quotation.vatAmount,
      netAmount: quotation.totalAmount,
      discountAmount: quotation.discountAmount || 0,
      discountLabel: quotation.discountPercent ? `Discount (${Number(quotation.discountPercent)}%)` : "Discount",
    },
    preparedByName: quotation.createdByName,
    approvedByName: quotation.approvedByName,
    approvedByDesignation: quotation.approvedByDesignation,
    approvedDate: quotation.approvedDate ? printDate(quotation.approvedDate) : null,
    signatureImageUrl: signatureImageUrl || undefined,
    coverLetterHtml: COVER_LETTER_TEXT,
    // Sanitized once here (not at every render site) - the same docProps
    // object feeds both the live preview and the print popup's
    // ReactDOMServer.renderToStaticMarkup pass.
    footerNotesHtml: quotation.remarks ? DOMPurify.sanitize(quotation.remarks) : undefined,
    ...(companyInfo && {
      companyName: companyInfo["Company.Name"] || undefined,
      companyAddress: companyInfo["Company.Address"] || undefined,
      companyPhone: companyInfo["Company.Phone"] || undefined,
      companyEmail: companyInfo["Company.Email"] || undefined,
    }),
  };
};

// A Quotation is only meaningful to hand to a customer once approved -
// SUBMITTED/REJECTED/CANCELLED have no business being printed/sent.
export const QUOTATION_PRINTABLE_STATUSES = ["APPROVED"];
